<template>
  <div data-view="home">
    <div class="tile is-ancestor has-text-centered">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div>
            <p class="title is-4">Play Dungeon Express</p>
            <div class="content">
              <p>
                Create a character and play offline using an interactive character sheet, or join an online room and play with your friends.
              </p>
            </div>
            <div class="content">
              <div class="buttons is-justify-content-center">
                <template v-if="joinState.status === OFFLINE">
                  <template v-if="characters.length">
                    <RouterLink class="button" :to="{ name: 'createCharacter' }" :disabled="(hostState.status !== OFFLINE) ? true : null">Create New</RouterLink>
                    <RouterLink class="button is-primary" :to="{ name: 'listCharacters' }" :disabled="(hostState.status !== OFFLINE) ? true : null">My Characters</RouterLink>
                  </template>
                  <template v-else>
                    <RouterLink class="button is-primary" :to="{ name: 'createCharacter' }" :disabled="(hostState.status !== OFFLINE) ? true : null">Create Character</RouterLink>
                  </template>
                </template>
                <template v-else>
                  <RouterLink class="button is-primary" :to="{ name: 'playCharacter', params: { id: joinState.characterId } }">Play Character</RouterLink>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div>
            <p class="title is-4">Host a Room</p>
            <div class="content">
              <p>Host an online room and act as game master for your friends.</p>
            </div>
            <div class="content">
              <div class="buttons is-justify-content-center">
                <RouterLink v-if="hostState.status !== OFFLINE" class="button is-primary" :to="{ name: 'room' }">Go to Room</RouterLink>
                <button v-else class="button is-primary" @click="openModal('hostRoom')" :disabled="(joinState.status !== OFFLINE) ? true : null">Host a Room</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="#modalContent">
    <!-- Manage effects -->
    <Modal name="hostRoom">
      <ModalCard :hide-close="hostState.status !== OFFLINE">
        <template v-slot:title>Host a Room</template>
        
        <template v-if="hostState.status === OFFLINE">
          <div class="field">
            <div class="control has-text-centered">
              Would you like to host an online room?
            </div>
          </div>
        </template>
        <template v-if="hostState.status === CONNECTING">
          <div class="field">
            <div class="control has-text-centered">
              Connecting to server...
            </div>
          </div>
        </template>
        <template v-if="hostState.status === CREATING_ROOM">
          <div class="field">
            <div class="control has-text-centered">
              Creating room...
            </div>
          </div>
        </template>
        <template v-if="hostState.status === JOINED">
          <div class="field">
            <div class="control has-text-centered roomCode" @click="copyRoomCode">
              <span class="title is-2">{{ roomCode }}</span><br>
              <small>- click to copy to clipboard -</small>
            </div>
          </div>
          <div class="field">
            <div class="control has-text-centered">
              Your room is ready! Give the room code above to your friends to start playing (you can view this code anytime).
            </div>
          </div>
        </template>

        <template v-slot:buttons>
          <button v-if="hostState.status === OFFLINE" class="button is-primary" @click="hostRoom">Host Room</button>
          <button v-else-if="hostState.status === CONNECTING || hostState.status === CREATING_ROOM" class="button is-primary is-loading" :disabled="true">Host Room</button>
          <button v-else-if="hostState.status === JOINED" class="button is-primary" @click="goToRoom">Go to Room</button>
        </template>
      </ModalCard>
    </Modal>
  </Teleport>
</template>

<script>
import { defineComponent, onMounted , onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import useCharacters from '@/hooks/useCharacters'
import { useHost, useJoin, OFFLINE, CONNECTING, CREATING_ROOM, JOINED } from '@/hooks/room'
import useModal from '@/hooks/useModal'
import useToast from '@/hooks/useToast'
import copyToClipboard from '@/helpers/copyToClipboard'

import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'

export default defineComponent({
  components: {
    Modal,
    ModalCard
  },

  setup () {
    const { characters } = useCharacters()
    const { openModal, closeModal, currentModal } = useModal()
    const toast = useToast()
    const router = useRouter()
    
    const { 
      state: hostState, 
      connect: connectHost, 
      createRoom,
      on: addHostEventHandler,
      off: removeHostEventHandler
    } = useHost()

    const {
      state: joinState
    } = useJoin()

    const roomCode = ref(null)

    const roomCreatedHandler = (room) => {
      roomCode.value = room.code
    }

    onMounted(() => {
      addHostEventHandler('roomCreated', roomCreatedHandler)
    })

    onUnmounted(() => {
      removeHostEventHandler(roomCreatedHandler)
    })

    const hostRoom = async () => {
      try {
        await connectHost()
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.warn(err)
        return
      }
      createRoom()
      currentModal.value.options.hideClose = true
    }

    const copyRoomCode = () => {
      toast('Room code copied.')
      copyToClipboard(roomCode.value)
    }

    const goToRoom = () => {
      closeModal()
      router.push({ name: 'room' })
    }

    return {
      characters,
      hostState,
      joinState,
      OFFLINE,
      CONNECTING,
      CREATING_ROOM,
      JOINED,
      openModal,
      hostRoom,
      roomCode,
      copyRoomCode,
      goToRoom
    }
  }
})
</script>

<style lang="sass" scoped>
.tile.is-child
  padding: 2.5rem

.card
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center

.title
  font-family: 'Playfair Display'

.roomCode
  user-select: text
  cursor: pointer
  padding: 1rem
  background-color: rgba(#000, 0.05)

@media screen and (min-width: 1024px)
  .tile.is-parent
    padding: 2.5rem
</style>
